import React, { useEffect } from 'react';
import Spinner from './Spinner';
import { badgeStyle, closeButtonStyle, dialogStyle, infoGridStyle, labelStyle, overlayStyle, valueStyle } from './detailsStyles';
import { fmtDate, fmtMoney } from './format';
import { amountStyle, cardStyle, cellStyle, headRowStyle, headStyle, headTextStyle, stripeStyle, tableStyle, textCellStyle } from './styles';

// Shown on top of the invoice dialog when it was opened from there
const orderOverlayStyle = { ...overlayStyle, zIndex: 1100 };

const getFields = (order) => {
    const address = [order.deliveryName, order.deliveryAddress1, `${order.deliveryZipCode || ''} ${order.deliveryCity || ''}`.trim()]
        .filter(Boolean)
        .join(', ');

    return [
        ['Kund', [order.customerNumber, order.customerName].filter(Boolean).join(' - ')],
        ['Orderdatum', fmtDate(order.orderDate)],
        ['Leveransdatum', fmtDate(order.deliveryDate)],
        ['Utleveransdatum', fmtDate(order.outboundDate)],
        ['Fakturanr', order.invoiceReference > 0 ? order.invoiceReference : 'Ej fakturerad'],
        ['Lagerklar', order.warehouseReady ? 'Ja' : 'Nej'],
        ['Vår referens', order.ourReference],
        ['Er referens', order.yourReference],
        ['Ert ordernummer', order.yourOrderNumber],
        ['Leveranssätt', order.wayOfDelivery],
        ['Leveransadress', address],
        ['Valuta', order.currency],
    ].filter(([, value]) => value);
};

const getTotals = (order) => [
    ['Netto', order.net],
    ['Frakt', order.freight],
    ['Moms', order.totalVAT],
    ['Totalt', order.total],
];

const OrderDetails = ({ documentNumber, order, loading, error, onClose }) => {
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return (
        <div style={orderOverlayStyle} onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={`Order ${documentNumber}`}
                style={dialogStyle}
                onClick={(e) => e.stopPropagation()}
            >
                <button type="button" aria-label="Stäng" onClick={onClose} style={closeButtonStyle}>
                    &times;
                </button>
                <h3 style={{ margin: 0 }}>
                    Order {documentNumber}
                    {order?.isWebOrder && <span style={badgeStyle('#1779ba')}>Webb</span>}
                    {order?.cancelled && <span style={badgeStyle('#cc4b37')}>Makulerad</span>}
                </h3>

                {loading && <p style={{ color: '#666', margin: '0.5rem 0 0' }}><Spinner />Hämtar order från Fortnox...</p>}
                {error && <p style={{ color: 'red', margin: '0.5rem 0 0' }}>Kunde inte hämta order {documentNumber}: {error}</p>}

                {order && (
                    <>
                        <div style={infoGridStyle}>
                            {getFields(order).map(([label, value]) => (
                                <div key={label}>
                                    <div style={labelStyle}>{label}</div>
                                    <div style={valueStyle}>{value}</div>
                                </div>
                            ))}
                        </div>

                        <div style={cardStyle}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr style={headRowStyle}>
                                        <th style={headTextStyle}>Artikelnr</th>
                                        <th style={headTextStyle}>Beskrivning</th>
                                        <th style={headStyle}>Beställt</th>
                                        <th style={headStyle}>Reserverat</th>
                                        <th style={headStyle}>Pris</th>
                                        <th style={headStyle}>Summa</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(order.rows || []).map((row, i) => (
                                        <tr key={i} style={stripeStyle(i)}>
                                            <td style={textCellStyle}>{row.articleNumber}</td>
                                            <td style={textCellStyle}>{row.description}</td>
                                            <td style={cellStyle}>{row.orderedQuantity}</td>
                                            <td style={cellStyle}>{row.reservedQuantity}</td>
                                            <td style={amountStyle(row.price)}>{fmtMoney(row.price)}</td>
                                            <td style={amountStyle(row.total)}>{fmtMoney(row.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <table style={{ width: 'auto', marginLeft: 'auto', marginTop: '1rem', fontSize: '0.85rem' }}>
                            <tbody>
                                {getTotals(order).map(([label, value], i, all) => {
                                    const isLast = i === all.length - 1;
                                    return (
                                        <tr key={label} style={isLast ? { fontWeight: 'bold', borderTop: '2px solid #333' } : undefined}>
                                            <td style={{ ...textCellStyle, paddingRight: '2rem' }}>{label}</td>
                                            <td style={amountStyle(value)}>{fmtMoney(value)}{order.currency ? ` ${order.currency}` : ''}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </div>
    );
};

export default OrderDetails;
